<template>
  <div class="rankingCell">
    <div class="mainComponent">
      <div class="rankWrap" v-if="rank > 0">
        <div class="rank">
          <p v-if="isRanker" :style="rankStyle" class="rank__ranker">
            {{ rank }}
          </p>
          <p v-else-if="isPrizeTarget" :style="rankStyle" class="rank__prize">
            {{ rank }}
          </p>
          <p v-else class="rank__normal">
            {{ rank }}<span>{{ suffix }}</span>
          </p>
        </div>
      </div>

      <streamer-icon
        class="iconArea"
        :streamer="streamer"
        :currentStatus="currentStatus"
        @firebaseEvent="logEvent"
      />

      <div class="mainInformation" @click="openProfile">
        <div class="mainInformation__content">
          <p class="mainInformation__name">{{ streamer.nickname }}</p>
          <p v-if="showScore" class="mainInformation__score">
            <span>{{ formattedScore }}</span>
          </p>
          <p v-if="broadcastTime" class="mainInformation__time">
            {{ broadcastTime }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import StreamerIcon from '@/components/StreamerIcon.vue'
import type { StreamerUser } from '@/models/StreamerUser'
import { CurrentStatus } from '@/models/StreamerInfo'
import { Client, defaultNativeInterfaceClient } from '@/utils/NativeInterface'
import formatter from '@/utils/Formatter'
import router from '@/router'
import { getRankOrdinalSuffix } from '@/i18n'
import { defaultFirebaseAnalytics } from '@/utils/firebase'
import { logEvent } from 'firebase/analytics'

const badgeImages: { [k: number]: string } = {
  1: '/img/badgeBgFirst.png',
  2: '/img/badgeBgSecond.png',
  3: '/img/badgeBgThird.png',
}

@Component({
  components: {
    StreamerIcon,
  },
})
export default class RankingEntry extends Vue {
  @Prop()
  streamer!: StreamerUser

  @Prop()
  currentStatus!: CurrentStatus | null

  @Prop({ default: 0 })
  rank!: number

  @Prop()
  score!: number

  @Prop({ default: false })
  isPrizeTarget!: boolean

  @Prop({ default: true })
  showScore!: boolean

  @Prop()
  broadcastSec!: number | null

  get isRanker() {
    return this.rank <= 3
  }

  get rankStyle() {
    const bgImage = badgeImages[this.rank]
    if (bgImage) {
      return {
        'background-image': `url(${bgImage})`,
      }
    }

    if (this.isPrizeTarget) {
      return {
        width: `calc(${String(this.rank).length - 1}em + 24px`,
      }
    }

    return null
  }

  get suffix() {
    return getRankOrdinalSuffix(this.rank)
  }

  get formattedScore() {
    return formatter.numberWithCommas(this.score)
  }

  get broadcastTime() {
    if (this.broadcastSec == null) return ''
    const hour = Math.floor(this.broadcastSec / 3600)
    const min = Math.floor((this.broadcastSec % 3600) / 60)

    const durationKey = hour > 0 ? 'durationFormatHourMin' : 'durationFormatMin'
    const duration = this.$t(durationKey, { hour, min })
    return this.$t('eventBroadcastTime', { duration })
  }

  openProfile() {
    this.logEvent(null)

    const nativeInterface = defaultNativeInterfaceClient()
    if (nativeInterface.client !== Client.native) {
      try {
        nativeInterface.openProfile(this.streamer.profileId)
        return
      } catch {
        // do nothing
      }
    }

    // webのプロフィールページに遷移する
    router.push({
      path: `/profile/${this.streamer.profileId}`,
    }).catch(() => {})
  }

  logEvent(stateType: string | null) {
    const params = {
      selected_vlive_id: this.streamer.vliveId,
      firebase_screen: 'WebView',
      state_type: stateType,
      url: location.href,
    }
    logEvent(defaultFirebaseAnalytics, 'select_state_type', params)
  }
}
</script>

<style scoped>
.rankingCell {
  border-bottom: 1px solid #eeeeee;
  color: #262626;
}

.mainComponent {
  display: flex;
  height: 76px;
  max-width: 500px;
  margin: 0 auto;
}

.rankWrap {
  display: inline-block;
  width: 58px;
  min-width: 58px;
  height: 24px;
  margin: 26px 4px 26px 16px;
}

.rank {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 24px;
}

.rank__ranker {
  background-size: contain;
  color: #ffffff;
  width: 24px;
  height: 24px;
  line-height: 24px;
  font-size: 14px;
  font-weight: 700;
}

.rank__prize {
  background: linear-gradient(135deg, #fab5e8, #a4e2fa);
  border-radius: 12px;
  color: #ffffff;
  height: 24px;
  line-height: 24px;
  font-size: 14px;
  font-weight: 700;
}

.rank__normal {
  vertical-align: middle;
  line-height: 16px;
  font-weight: 600;
}

.rank__normal span {
  font-size: 12px;
}

.iconArea {
  margin-left: 4px;
  height: 76px;
}

.mainInformation {
  display: inline-block;
  margin: 0 22px 0 12px;
  min-width: 217px;
}

.mainInformation__content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  height: 76px;
}

.mainInformation__content p {
  flex-basis: 100%;
}

.mainInformation__name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mainInformation__score {
  font-weight: 600;
  font-size: 12px;
  vertical-align: middle;
  line-height: 150%;
}

.mainInformation__score span {
  color: #188dff;
}

.mainInformation__time {
  font-size: 12px;
  vertical-align: middle;
  line-height: 150%;
  color: #808080;
}
</style>
